import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

const ACCEPTED_EXTENSIONS = ['.pdf', '.jpeg', '.jpg', '.png'];

/** Matches detail/edit pages (UUID segment) or upsert "new" pages. */
const EXCLUDED_PAGE_PATTERN = /\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|new)(\/|$)/i;

@Injectable({ providedIn: 'root' })
export class GlobalDropZoneService {
  private readonly router = inject(Router);

  readonly isDragging = signal(false);
  readonly droppedFiles = signal<File[]>([]);
  readonly showConfirmModal = signal(false);

  private dragCounter = 0;
  private initialized = false;

  initialize(): void {
    if (this.initialized) return;
    this.initialized = true;

    document.addEventListener('dragenter', (e) => this.onDragEnter(e));
    document.addEventListener('dragover', (e) => this.onDragOver(e));
    document.addEventListener('dragleave', (e) => this.onDragLeave(e));
    document.addEventListener('drop', (e) => this.onDrop(e));
  }

  private isDetailPage(): boolean {
    return EXCLUDED_PAGE_PATTERN.test(this.router.url);
  }

  addFiles(files: File[]): void {
    const accepted = files.filter(f => this.isAcceptedFile(f));
    if (accepted.length === 0) return;
    this.droppedFiles.update(current => [...current, ...accepted]);
  }

  removeFile(index: number): void {
    this.droppedFiles.update(current => current.filter((_, i) => i !== index));
  }

  clearFiles(): void {
    this.droppedFiles.set([]);
  }

  private onDragEnter(e: DragEvent): void {
    if (!this.hasFiles(e) || this.isDetailPage()) return;
    e.preventDefault();
    this.dragCounter++;
    this.isDragging.set(true);
  }

  private onDragOver(e: DragEvent): void {
    if (!this.hasFiles(e) || this.isDetailPage()) return;
    e.preventDefault();
  }

  private onDragLeave(e: DragEvent): void {
    if (!this.hasFiles(e)) return;
    e.preventDefault();
    this.dragCounter--;
    if (this.dragCounter <= 0) {
      this.dragCounter = 0;
      this.isDragging.set(false);
    }
  }

  private onDrop(e: DragEvent): void {
    e.preventDefault();
    this.dragCounter = 0;
    this.isDragging.set(false);

    if (this.isDetailPage()) return;

    const files = Array.from(e.dataTransfer?.files ?? []);
    const accepted = files.filter(f => this.isAcceptedFile(f));
    if (accepted.length === 0) return;

    this.droppedFiles.set(accepted);
    this.showConfirmModal.set(true);
  }

  private hasFiles(e: DragEvent): boolean {
    return e.dataTransfer?.types?.includes('Files') ?? false;
  }

  private isAcceptedFile(file: File): boolean {
    const name = file.name.toLowerCase();
    return ACCEPTED_EXTENSIONS.some(ext => name.endsWith(ext));
  }
}
